import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Layers, Scale, Droplets, Shield, Volume2, Ruler, Thermometer, Flame, Wrench, Sparkles, Weight, Percent, Target, Maximize, Tag, FileText, X, Upload, Trash2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config';

// Application Card with admin upload support
const AppCard = ({ item, idx, productId, isAdmin, appImages, uploadingIdx, onUpload, onDelete, onLightbox }) => {
  const fileRef = useRef(null);
  const text = typeof item === 'string' ? item : item.text;
  const hardcodedImage = typeof item === 'object' && item.image ? item.image : null;
  const dynamicImage = appImages[String(idx)];
  const image = dynamicImage || hardcodedImage;

  return image ? (
    <div key={idx} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden relative">
      <div className="w-full h-72 overflow-hidden bg-[#F5F5F0] cursor-pointer" onClick={() => onLightbox(image, text)}>
        <img src={image} alt={text} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
      </div>
      {isAdmin && (
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) onUpload(idx, e.target.files[0]); }} />
          <button onClick={() => fileRef.current?.click()} className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow transition-all" data-testid={`app-upload-${idx}`}>
            {uploadingIdx === idx ? <div className="w-4 h-4 border-2 border-[#061d36] border-t-transparent rounded-full animate-spin" /> : <Upload className="w-4 h-4 text-[#061d36]" />}
          </button>
          {dynamicImage && (
            <button onClick={() => onDelete(idx)} className="p-1.5 bg-white/90 hover:bg-red-50 rounded-lg shadow transition-all" data-testid={`app-delete-${idx}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          )}
        </div>
      )}
      <div className="flex items-center gap-3 p-4">
        <div className="w-3 h-3 bg-[#061d36] rounded-full flex-shrink-0"></div>
        <span className="text-[#061d36]/80 font-medium">{text}</span>
      </div>
    </div>
  ) : (
    <div key={idx} className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow relative">
      <div className="w-3 h-3 bg-[#061d36] rounded-full flex-shrink-0"></div>
      <span className="text-[#061d36]/80 font-medium">{text}</span>
      {isAdmin && (
        <div className="absolute top-2 right-2 z-10">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) onUpload(idx, e.target.files[0]); }} />
          <button onClick={() => fileRef.current?.click()} className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow transition-all" data-testid={`app-upload-${idx}`}>
            {uploadingIdx === idx ? <div className="w-4 h-4 border-2 border-[#061d36] border-t-transparent rounded-full animate-spin" /> : <Upload className="w-4 h-4 text-[#061d36]" />}
          </button>
        </div>
      )}
    </div>
  );
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const { language } = useLanguage();
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxLoaded, setLightboxLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [appImages, setAppImages] = useState({});
  const [uploadingIdx, setUploadingIdx] = useState(null);
  

  useEffect(() => {
    const session = localStorage.getItem('admin_session');
    if (session) setIsAdmin(true);
  }, []);

  const fetchAppImages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/applications/images/${productId}`);
      const data = await res.json();
      const map = {};
      data.forEach(img => { map[img.app_index] = img.image_url; });
      setAppImages(map);
    } catch (e) { console.error(e); }
  }, [API_URL, productId]);

  useEffect(() => { fetchAppImages(); }, [fetchAppImages]);

  const handleAppImageUpload = async (idx, file) => {
    const session = localStorage.getItem('admin_session');
    if (!session || !file) return;
    setUploadingIdx(idx);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/applications/upload-image?product_id=${productId}&app_index=${idx}&session_id=${session}`, { method: 'POST', body: formData });
      if (res.ok) await fetchAppImages();
    } catch (e) { console.error(e); }
    setUploadingIdx(null);
  };

  const handleAppImageDelete = async (idx) => {
    const session = localStorage.getItem('admin_session');
    if (!session) return;
    try {
      const res = await fetch(`${API_URL}/api/applications/delete-image?product_id=${productId}&app_index=${idx}&session_id=${session}`, { method: 'DELETE' });
      if (res.ok) await fetchAppImages();
    } catch (e) { console.error(e); }
  };

  // Technical specifications data for each product type
  const productData = {
    gkl: {
      ru: {
        title: 'На основе ГКЛ',
        fullTitle: 'Панели ТМ "Гипсовинил" на основе ГСП с ПВХ покрытием',
        description: 'Панели на основе специального гипсокартона с прямой кромкой (ГСП) и износостойким ПВХ покрытием. Идеальное решение для внутренней отделки офисов, отелей, коммерческих и административных помещений.',
        features: ['Износостойкость', 'Экологичность', 'Доступная цена', 'Простой монтаж', 'Эксплуатация более 15 лет'],
        keySpecs: [
          { label: 'Название', value: 'Панели ТМ "Гипсовинил" на основе ГСП с ПВХ покрытием', icon: 'tag' },
          { label: 'Состав', value: 'Гипсовая строительная плита (гипсокартон): гипсовый сердечник и с двух сторон картон', icon: 'shield' },
          { label: 'Код ТНВЭД', value: '6808 00 000 0', icon: 'file' },
          { label: 'Размеры', value: '12,5×1200×2500мм; 12,5×1200×2700мм; 12,5×1200×3000мм', icon: 'maximize' },
          { label: 'Огнестойкость', value: 'КМ2, Г1', icon: 'flame' },
          { label: 'Плотность', value: '860 кг/м³', icon: 'scale' },
          { label: 'Шумоизоляция', value: '28-32 дБ', icon: 'volume' },
          { label: 'Влагостойкость', value: 'Моющееся ПВХ покрытие', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Вес', value: '9,5 кг/м²' },
          { label: 'Отпускная влажность', value: '1%' },
          { label: 'Твёрдость', value: '1,8 МПа' },
          { label: 'Предел прочности при изгибе', value: '2 МПа' },
          { label: 'Удельное сопротивление выдёргиванию шурупов', value: '20 Н/мм' },
          { label: 'Температурный коэффициент линейного расширения', value: '0,5%' }
        ],
        applications: ['Офисные помещения', 'Коммерческие объекты', 'Образовательные учреждения']
      },
      kz: {
        title: 'ГКЛ негізінде',
        fullTitle: 'ГСП негізіндегі ПВХ жабыны бар "Гипсовинил" ТМ панельдері',
        description: 'Тікелей жиекті (ГСП) және тозуға төзімді ПВХ жабыны бар арнайы гипсокартон негізіндегі панельдер. Кеңселер, коммерциялық және тұрғын үй-жайларды ішкі әрлеу үшін тамаша шешім.',
        features: ['Тозуға төзімділік', 'Экологиялық таза', 'Қолжетімді баға', 'Қарапайым монтаж', '15 жылдан астам пайдалану'],
        keySpecs: [
          { label: 'Атауы', value: 'ГСП негізіндегі ПВХ жабыны бар "Гипсовинил" ТМ панельдері', icon: 'tag' },
          { label: 'Құрамы', value: 'Гипс құрылыс тақтасы (гипсокартон): гипс өзегі және екі жағынан картон', icon: 'shield' },
          { label: 'ТНВЭД коды', value: '6808 00 000 0', icon: 'file' },
          { label: 'Өлшемдері', value: '12,5×1200×2500мм; 12,5×1200×2700мм; 12,5×1200×3000мм', icon: 'maximize' },
          { label: 'Өртке төзімділік', value: 'КМ2, Г1', icon: 'flame' },
          { label: 'Тығыздық', value: '860 кг/м³', icon: 'scale' },
          { label: 'Шуыл оқшаулау', value: '28-32 дБ', icon: 'volume' },
          { label: 'Ылғалға төзімділік', value: 'Жуылатын ПВХ жабыны', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Салмақ', value: '9,5 кг/м²' },
          { label: 'Шығару ылғалдылығы', value: '1%' },
          { label: 'Қаттылық', value: '1,8 МПа' },
          { label: 'Иілу беріктігі', value: '2 МПа' },
          { label: 'Бұранданы суыру кедергісі', value: '20 Н/мм' },
          { label: 'Сызықтық кеңею коэффициенті', value: '0,5%' }
        ],
        applications: ['Кеңсе үй-жайлары', 'Коммерциялық объектілер', 'Білім беру мекемелері']
      },
      en: {
        title: 'GKL Based',
        fullTitle: 'GIPSOVINIL.KZ Panels based on GSP with PVC coating',
        description: 'Panels based on special gypsum board with straight edge (GSP) and wear-resistant PVC coating. Perfect solution for interior finishing of offices, commercial and residential spaces.',
        features: ['Wear resistance', 'Eco-friendly', 'Affordable price', 'Easy installation', 'Service life over 15 years'],
        keySpecs: [
          { label: 'Name', value: 'GIPSOVINIL.KZ Panels based on GSP with PVC coating', icon: 'tag' },
          { label: 'Composition', value: 'Gypsum construction board (drywall): gypsum core with cardboard on both sides', icon: 'shield' },
          { label: 'HS Code', value: '6808 00 000 0', icon: 'file' },
          { label: 'Dimensions', value: '12.5×1200×2500mm; 12.5×1200×2700mm; 12.5×1200×3000mm', icon: 'maximize' },
          { label: 'Fire resistance', value: 'KM2, G1', icon: 'flame' },
          { label: 'Density', value: '860 kg/m³', icon: 'scale' },
          { label: 'Sound insulation', value: '28-32 dB', icon: 'volume' },
          { label: 'Moisture resistance', value: 'Washable PVC coating', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Weight', value: '9.5 kg/m²' },
          { label: 'Release humidity', value: '1%' },
          { label: 'Hardness', value: '1.8 MPa' },
          { label: 'Bending strength', value: '2 MPa' },
          { label: 'Screw pull-out resistance', value: '20 N/mm' },
          { label: 'Linear expansion coefficient', value: '0.5%' }
        ],
        applications: ['Office spaces', 'Commercial facilities', 'Educational institutions']
      },
      ar: {
        title: 'مستندة إلى GKL',
        fullTitle: 'ألواح GIPSOVINIL.KZ المستندة إلى GSP مع طلاء PVC',
        description: 'ألواح مستندة إلى لوح جبس خاص ذي حافة مستقيمة (GSP) وطلاء PVC مقاوم للتآكل. الحل الأمثل للتشطيب الداخلي للمكاتب والمساحات التجارية والسكنية.',
        features: ['مقاومة التآكل', 'صديقة للبيئة', 'سعر تنافسي', 'سهولة التركيب', 'عمر افتراضي يتجاوز 15 عاماً'],
        keySpecs: [
          { label: 'الاسم', value: 'ألواح GIPSOVINIL.KZ المستندة إلى GSP مع طلاء PVC', icon: 'tag' },
          { label: 'التركيبة', value: 'لوح جبس إنشائي (ألواح جبس): قلب جبسي مع كرتون على الوجهين', icon: 'shield' },
          { label: 'رمز النظام المنسق', value: '6808 00 000 0', icon: 'file' },
          { label: 'الأبعاد', value: '12.5×1200×2500 مم؛ 12.5×1200×2700 مم؛ 12.5×1200×3000 مم', icon: 'maximize' },
          { label: 'مقاومة الحريق', value: 'KM2, G1', icon: 'flame' },
          { label: 'الكثافة', value: '860 كجم/م³', icon: 'scale' },
          { label: 'العزل الصوتي', value: '28-32 ديسيبل', icon: 'volume' },
          { label: 'مقاومة الرطوبة', value: 'طلاء PVC قابل للغسل', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'الوزن', value: '9.5 كجم/م²' },
          { label: 'رطوبة الإصدار', value: '1%' },
          { label: 'الصلابة', value: '1.8 ميغاباسكال' },
          { label: 'مقاومة الانحناء', value: '2 ميغاباسكال' },
          { label: 'مقاومة سحب البراغي', value: '20 ن/مم' },
          { label: 'معامل التمدد الخطي', value: '0.5%' }
        ],
        applications: ['المساحات المكتبية', 'المنشآت التجارية', 'المؤسسات التعليمية']
      }
    },
    comfort: {
      ru: {
        title: 'Комфорт',
        fullTitle: 'Панели Комфорт ТМ "Гипсовинил" на основе ГСП типа DFHЗIR с ПВХ покрытием',
        description: 'Панели на основе влагостойкого и пожаробезопасного гипсокартона со стекловолокном. Имеют повышенные ударопрочные свойства и подходят для помещений с высокой проходимостью.',
        features: ['Влагостойкость', 'Пожаробезопасность', 'Долговечность', 'Ударопрочность'],
        keySpecs: [
          { label: 'Название', value: 'Панели Комфорт ТМ "Гипсовинил" на основе ГСП типа DFHЗIR с ПВХ покрытием', icon: 'tag' },
          { label: 'Состав', value: 'Гипсовая строительная плита: влагостойкий картон, гипсовый сердечник, армированный стекловолокном', icon: 'shield' },
          { label: 'Код ТНВЭД', value: '6808 00 000 0', icon: 'file' },
          { label: 'Размеры', value: '12,5×1200×2500мм; 12,5×1200×2700мм; 12,5×1200×3000мм', icon: 'maximize' },
          { label: 'Огнестойкость', value: 'КМ1, Г1', icon: 'flame' },
          { label: 'Плотность', value: '1200 кг/м³', icon: 'scale' },
          { label: 'Шумоизоляция', value: '20-30 дБ', icon: 'volume' },
          { label: 'Влагостойкость', value: 'Влагостойкий гипсокартон и моющееся ПВХ покрытие', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Вес', value: '12 кг/м²' },
          { label: 'Отпускная влажность', value: '2%' },
          { label: 'Твёрдость', value: '7 МПа' },
          { label: 'Предел прочности при изгибе', value: '3-7 МПа' },
          { label: 'Удельное сопротивление выдёргиванию шурупов', value: '34 Н/мм' },
          { label: 'Температурный коэффициент линейного расширения', value: '0,25%' }
        ],
        applications: ['Медицинские учреждения', 'Торговые центры', 'Гостиницы', 'Рестораны и кафе']
      },
      kz: {
        title: 'Комфорт',
        fullTitle: 'ПВХ жабыны бар DFHЗIR типті ГСП негізіндегі "Гипсовинил" ТМ Комфорт панельдері',
        description: 'Шыны талшық қосылған ылғалға төзімді және өртке қауіпсіз гипсокартон негізіндегі панельдер. Жоғары соққыға төзімділік қасиеттері бар және жоғары өткізу қабілеті бар үй-жайлар үшін жарамды.',
        features: ['Ылғалға төзімділік', 'Өрт қауіпсіздігі', 'Ұзақ мерзімділік', 'Соққыға төзімділік'],
        keySpecs: [
          { label: 'Атауы', value: 'ПВХ жабыны бар DFHЗIR типті ГСП негізіндегі "Гипсовинил" ТМ Комфорт панельдері', icon: 'tag' },
          { label: 'Құрамы', value: 'Гипс құрылыс тақтасы: ылғалға төзімді картон, шыны талшықпен армирленген гипс өзегі', icon: 'shield' },
          { label: 'ТНВЭД коды', value: '6808 00 000 0', icon: 'file' },
          { label: 'Өлшемдері', value: '12,5×1200×2500мм; 12,5×1200×2700мм; 12,5×1200×3000мм', icon: 'maximize' },
          { label: 'Өртке төзімділік', value: 'КМ1, Г1', icon: 'flame' },
          { label: 'Тығыздық', value: '1200 кг/м³', icon: 'scale' },
          { label: 'Шуыл оқшаулау', value: '20-30 дБ', icon: 'volume' },
          { label: 'Ылғалға төзімділік', value: 'Ылғалға төзімді гипсокартон және жуылатын ПВХ жабыны', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Салмақ', value: '12 кг/м²' },
          { label: 'Шығару ылғалдылығы', value: '2%' },
          { label: 'Қаттылық', value: '7 МПа' },
          { label: 'Иілу беріктігі', value: '3-7 МПа' },
          { label: 'Бұранданы суыру кедергісі', value: '34 Н/мм' },
          { label: 'Сызықтық кеңею коэффициенті', value: '0,25%' }
        ],
        applications: ['Медициналық мекемелер', 'Сауда орталықтары', 'Қонақ үйлер', 'Мейрамханалар мен кафелер']
      },
      en: {
        title: 'Comfort',
        fullTitle: 'Comfort GIPSOVINIL.KZ Panels based on DFHЗIR type GSP with PVC coating',
        description: 'Panels based on moisture-resistant and fire-safe gypsum board with fiberglass. Have enhanced impact resistance properties and are suitable for high-traffic areas.',
        features: ['Moisture resistance', 'Fire safety', 'Longevity', 'Impact resistance'],
        keySpecs: [
          { label: 'Name', value: 'Comfort GIPSOVINIL.KZ Panels based on DFHЗIR type GSP with PVC coating', icon: 'tag' },
          { label: 'Composition', value: 'Gypsum construction board: moisture-resistant cardboard, fiberglass-reinforced gypsum core', icon: 'shield' },
          { label: 'HS Code', value: '6808 00 000 0', icon: 'file' },
          { label: 'Dimensions', value: '12.5×1200×2500mm; 12.5×1200×2700mm; 12.5×1200×3000mm', icon: 'maximize' },
          { label: 'Fire resistance', value: 'KM1, G1', icon: 'flame' },
          { label: 'Density', value: '1200 kg/m³', icon: 'scale' },
          { label: 'Sound insulation', value: '20-30 dB', icon: 'volume' },
          { label: 'Moisture resistance', value: 'Moisture-resistant drywall and washable PVC coating', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Weight', value: '12 kg/m²' },
          { label: 'Release humidity', value: '2%' },
          { label: 'Hardness', value: '7 MPa' },
          { label: 'Bending strength', value: '3-7 MPa' },
          { label: 'Screw pull-out resistance', value: '34 N/mm' },
          { label: 'Linear expansion coefficient', value: '0.25%' }
        ],
        applications: ['Healthcare facilities', 'Shopping centers', 'Hotels', 'Restaurants and cafes']
      },
      ar: {
        title: 'Comfort',
        fullTitle: 'ألواح Comfort من GIPSOVINIL.KZ المستندة إلى GSP من نوع DFHЗIR مع طلاء PVC',
        description: 'ألواح مستندة إلى لوح جبس مقاوم للرطوبة وآمن من الحريق مع ألياف زجاجية. تتمتع بخصائص مقاومة صدمات معززة وتناسب المناطق عالية الحركة.',
        features: ['مقاومة الرطوبة', 'السلامة من الحريق', 'طول العمر الافتراضي', 'مقاومة الصدمات'],
        keySpecs: [
          { label: 'الاسم', value: 'ألواح Comfort من GIPSOVINIL.KZ المستندة إلى GSP من نوع DFHЗIR مع طلاء PVC', icon: 'tag' },
          { label: 'التركيبة', value: 'لوح جبس إنشائي: كرتون مقاوم للرطوبة، قلب جبسي مسلّح بالألياف الزجاجية', icon: 'shield' },
          { label: 'رمز النظام المنسق', value: '6808 00 000 0', icon: 'file' },
          { label: 'الأبعاد', value: '12.5×1200×2500 مم؛ 12.5×1200×2700 مم؛ 12.5×1200×3000 مم', icon: 'maximize' },
          { label: 'مقاومة الحريق', value: 'KM1, G1', icon: 'flame' },
          { label: 'الكثافة', value: '1200 كجم/م³', icon: 'scale' },
          { label: 'العزل الصوتي', value: '20-30 ديسيبل', icon: 'volume' },
          { label: 'مقاومة الرطوبة', value: 'ألواح جبس مقاومة للرطوبة وطلاء PVC قابل للغسل', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'الوزن', value: '12 كجم/م²' },
          { label: 'رطوبة الإصدار', value: '2%' },
          { label: 'الصلابة', value: '7 ميغاباسكال' },
          { label: 'مقاومة الانحناء', value: '3-7 ميغاباسكال' },
          { label: 'مقاومة سحب البراغي', value: '34 ن/مم' },
          { label: 'معامل التمدد الخطي', value: '0.25%' }
        ],
        applications: ['المنشآت الصحية', 'مراكز التسوق', 'الفنادق', 'المطاعم والمقاهي']
      }
    },
    sml: {
      ru: {
        title: 'На основе СМЛ',
        fullTitle: 'Панели ТМ "Гипсовинил" на основе стекломагниевого листа с ПВХ покрытием',
        description: 'Самые влагостойкие панели с ПВХ покрытием, ударопрочные и слабогорючие. Идеальны для объектов с повышенными требованиями к пожарной безопасности.',
        features: ['Высокая огнестойкость', 'Износостойкость', 'Экологичность', 'Максимальная влагостойкость'],
        keySpecs: [
          { label: 'Название', value: 'Панели ТМ "Гипсовинил" на основе стекломагниевого листа с ПВХ покрытием', icon: 'tag' },
          { label: 'Состав', value: 'Стекломагниевый лист: оксид магния, хлорид магния, древесная стружка, перлит, стеклоткань', icon: 'shield' },
          { label: 'Код ТНВЭД', value: '6808 00 000 0', icon: 'file' },
          { label: 'Размеры', value: '6×1220×2440мм; 8×1220×2440мм; 10×1220×2440мм', icon: 'maximize' },
          { label: 'Огнестойкость', value: 'КМ1, Г1', icon: 'flame' },
          { label: 'Плотность', value: '950-1200 кг/м³', icon: 'scale' },
          { label: 'Шумоизоляция', value: '35-40 дБ', icon: 'volume' },
          { label: 'Влагостойкость', value: 'Максимальная, <5% водопоглощение', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Вес', value: '6мм — 6 кг/м²; 8мм — 8 кг/м²; 10мм — 9,5 кг/м²' },
          { label: 'Отпускная влажность', value: '<5%' },
          { label: 'Твёрдость', value: '8,3 МПа' },
          { label: 'Предел прочности при изгибе', value: '7-12 МПа' },
          { label: 'Удельное сопротивление выдёргиванию шурупов', value: '10-15 Н/мм' },
          { label: 'Температурный коэффициент линейного расширения', value: '0,0-0,05%' }
        ],
        applications: ['Государственные учреждения', 'Объекты с повышенной пожароопасностью', 'Метро и аэропорты', 'Промышленные объекты']
      },
      kz: {
        title: 'СМЛ негізінде',
        fullTitle: 'ПВХ жабыны бар шыны-магний парағы негізіндегі "Гипсовинил" ТМ панельдері',
        description: 'ПВХ жабыны бар ең ылғалға төзімді панельдер, соққыға төзімді және әлсіз жанғыш. Өрт қауіпсіздігіне жоғары талаптар қойылатын объектілер үшін тамаша.',
        features: ['Жоғары өртке төзімділік', 'Тозуға төзімділік', 'Экологиялық таза', 'Максималды ылғалға төзімділік'],
        keySpecs: [
          { label: 'Атауы', value: 'ПВХ жабыны бар шыны-магний парағы негізіндегі "Гипсовинил" ТМ панельдері', icon: 'tag' },
          { label: 'Құрамы', value: 'Шыны-магний парағы: магний оксиді, магний хлориді, ағаш жоңқасы, перлит, шыны мата', icon: 'shield' },
          { label: 'ТНВЭД коды', value: '6808 00 000 0', icon: 'file' },
          { label: 'Өлшемдері', value: '6×1220×2440мм; 8×1220×2440мм; 10×1220×2440мм', icon: 'maximize' },
          { label: 'Өртке төзімділік', value: 'КМ1, Г1', icon: 'flame' },
          { label: 'Тығыздық', value: '950-1200 кг/м³', icon: 'scale' },
          { label: 'Шуыл оқшаулау', value: '35-40 дБ', icon: 'volume' },
          { label: 'Ылғалға төзімділік', value: 'Максималды, <5% су сіңіру', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Салмақ', value: '6мм — 6 кг/м²; 8мм — 8 кг/м²; 10мм — 9,5 кг/м²' },
          { label: 'Шығару ылғалдылығы', value: '<5%' },
          { label: 'Қаттылық', value: '8,3 МПа' },
          { label: 'Иілу беріктігі', value: '7-12 МПа' },
          { label: 'Бұранданы суыру кедергісі', value: '10-15 Н/мм' },
          { label: 'Сызықтық кеңею коэффициенті', value: '0,0-0,05%' }
        ],
        applications: ['Мемлекеттік мекемелер', 'Өрт қаупі жоғары объектілер', 'Метро және әуежайлар', 'Өнеркәсіптік объектілер']
      },
      en: {
        title: 'SML Based',
        fullTitle: 'GIPSOVINIL.KZ Panels based on glass-magnesium sheet with PVC coating',
        description: 'The most moisture-resistant panels with PVC coating, impact-resistant and low-flammable. Ideal for facilities with high fire safety requirements.',
        features: ['High fire resistance', 'Wear resistance', 'Eco-friendly', 'Maximum moisture resistance'],
        keySpecs: [
          { label: 'Name', value: 'GIPSOVINIL.KZ Panels based on glass-magnesium sheet with PVC coating', icon: 'tag' },
          { label: 'Composition', value: 'Glass-magnesium sheet: magnesium oxide, magnesium chloride, wood chips, perlite, fiberglass cloth', icon: 'shield' },
          { label: 'HS Code', value: '6808 00 000 0', icon: 'file' },
          { label: 'Dimensions', value: '6×1220×2440mm; 8×1220×2440mm; 10×1220×2440mm', icon: 'maximize' },
          { label: 'Fire resistance', value: 'KM1, G1', icon: 'flame' },
          { label: 'Density', value: '950-1200 kg/m³', icon: 'scale' },
          { label: 'Sound insulation', value: '35-40 dB', icon: 'volume' },
          { label: 'Moisture resistance', value: 'Maximum, <5% water absorption', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'Weight', value: '6mm — 6 kg/m²; 8mm — 8 kg/m²; 10mm — 9.5 kg/m²' },
          { label: 'Release humidity', value: '<5%' },
          { label: 'Hardness', value: '8.3 MPa' },
          { label: 'Bending strength', value: '7-12 MPa' },
          { label: 'Screw pull-out resistance', value: '10-15 N/mm' },
          { label: 'Linear expansion coefficient', value: '0.0-0.05%' }
        ],
        applications: ['Government facilities', 'High fire risk facilities', 'Metro and airports', 'Industrial facilities']
      },
      ar: {
        title: 'مستندة إلى SML',
        fullTitle: 'ألواح GIPSOVINIL.KZ المستندة إلى الصفيحة الزجاجية المغنيسيومية مع طلاء PVC',
        description: 'الألواح الأعلى مقاومةً للرطوبة مع طلاء PVC، مقاومة للصدمات ومنخفضة الاشتعال. الخيار الأمثل للمنشآت ذات متطلبات السلامة من الحريق الصارمة.',
        features: ['مقاومة حريق عالية', 'مقاومة التآكل', 'صديقة للبيئة', 'أقصى درجات مقاومة الرطوبة'],
        keySpecs: [
          { label: 'الاسم', value: 'ألواح GIPSOVINIL.KZ المستندة إلى الصفيحة الزجاجية المغنيسيومية مع طلاء PVC', icon: 'tag' },
          { label: 'التركيبة', value: 'الصفيحة الزجاجية المغنيسيومية: أكسيد المغنيسيوم، كلوريد المغنيسيوم، نشارة الخشب، البيرلايت، قماش الألياف الزجاجية', icon: 'shield' },
          { label: 'رمز النظام المنسق', value: '6808 00 000 0', icon: 'file' },
          { label: 'الأبعاد', value: '6×1220×2440 مم؛ 8×1220×2440 مم؛ 10×1220×2440 مم', icon: 'maximize' },
          { label: 'مقاومة الحريق', value: 'KM1, G1', icon: 'flame' },
          { label: 'الكثافة', value: '950-1200 كجم/م³', icon: 'scale' },
          { label: 'العزل الصوتي', value: '35-40 ديسيبل', icon: 'volume' },
          { label: 'مقاومة الرطوبة', value: 'أقصى درجة، امتصاص مائي أقل من 5%', icon: 'droplets' }
        ],
        detailedSpecs: [
          { label: 'الوزن', value: '6 مم — 6 كجم/م²؛ 8 مم — 8 كجم/م²؛ 10 مم — 9.5 كجم/م²' },
          { label: 'رطوبة الإصدار', value: 'أقل من 5%' },
          { label: 'الصلابة', value: '8.3 ميغاباسكال' },
          { label: 'مقاومة الانحناء', value: '7-12 ميغاباسكال' },
          { label: 'مقاومة سحب البراغي', value: '10-15 ن/مم' },
          { label: 'معامل التمدد الخطي', value: '0.0-0.05%' }
        ],
        applications: ['المنشآت الحكومية', 'المنشآت عالية خطر الحريق', 'المترو والمطارات', 'المنشآت الصناعية']
      }
    },
    fiber: {
      ru: {
        title: 'На основе фиброцемента',
        fullTitle: 'Панели ТМ "Гипсовинил" на основе фиброцементной плиты с ПВХ покрытием',
        description: 'Аналог СМЛ плиты по техническим характеристикам с высотой листа до 3м. Максимальная прочность и долговечность для самых требовательных проектов.',
        features: ['Прочность', 'Долговечность', 'Влагостойкость', 'Высота до 3м'],
        keySpecs: [
          { label: 'Размеры', value: '8×1200×3000мм', icon: 'maximize' },
          { label: 'Плотность', value: '1600 кг/м³', icon: 'layers' },
          { label: 'Огнестойкость', value: 'КМ1, Г1', icon: 'flame' },
          { label: 'Влагостойкость', value: '3%', icon: 'droplets' },
          { label: 'Шумоизоляция', value: '27-31 дБ', icon: 'volume' },
          { label: 'Вес', value: '13 кг/м²', icon: 'weight' }
        ],
        detailedSpecs: [
          { label: 'Описание', value: 'Аналог СМЛ плиты по техническим характеристикам, с высотой листа 3м' },
          { label: 'Состав панелей', value: 'Фиброцементная плита (цемент, песок, целлюлозные волокна)' },
          { label: 'Размеры', value: '8х1200х3000мм' },
          { label: 'Плотность', value: '1600 кг/м³' },
          { label: 'Вес', value: '13 кг/м²' },
          { label: 'Отпускная влажность', value: '3%' },
          { label: 'Твёрдость', value: '19 МПа' },
          { label: 'Предел прочности при изгибе', value: '20 МПа' },
          { label: 'Индекс шумоизоляции', value: '27-31 дБ' },
          { label: 'Удельное сопротивление выдёргиванию шурупов', value: '35-40 Н/мм' },
          { label: 'Температурный коэффициент линейного расширения', value: '0-0,02%' },
          { label: 'Класс пожарной опасности', value: 'КМ1, Г1' }
        ],
        applications: ['Высотные здания', 'Фасады', 'Чистые помещения', 'Лаборатории']
      },
      kz: {
        title: 'Фиброцемент негізінде',
        fullTitle: 'ПВХ жабыны бар фиброцемент тақтасы негізіндегі "Гипсовинил" ТМ панельдері',
        description: 'Техникалық сипаттамалары бойынша СМЛ тақтасының аналогы, парақ биіктігі 3 м-ге дейін. Ең талапшыл жобалар үшін максималды беріктік және ұзақ мерзімділік.',
        features: ['Беріктік', 'Ұзақ мерзімділік', 'Ылғалға төзімділік', 'Биіктігі 3 м-ге дейін'],
        keySpecs: [
          { label: 'Өлшемдері', value: '8×1200×3000мм', icon: 'maximize' },
          { label: 'Тығыздық', value: '1600 кг/м³', icon: 'layers' },
          { label: 'Өртке төзімділік', value: 'КМ1, Г1', icon: 'flame' },
          { label: 'Ылғалға төзімділік', value: '3%', icon: 'droplets' },
          { label: 'Шуыл оқшаулау', value: '27-31 дБ', icon: 'volume' },
          { label: 'Салмақ', value: '13 кг/м²', icon: 'weight' }
        ],
        detailedSpecs: [
          { label: 'Сипаттама', value: 'Техникалық сипаттамалары бойынша СМЛ тақтасының аналогы, парақ биіктігі 3м' },
          { label: 'Панель құрамы', value: 'Фиброцемент тақтасы (цемент, құм, целлюлоза талшықтары)' },
          { label: 'Өлшемдері', value: '8х1200х3000мм' },
          { label: 'Тығыздық', value: '1600 кг/м³' },
          { label: 'Салмақ', value: '13 кг/м²' },
          { label: 'Шығару ылғалдылығы', value: '3%' },
          { label: 'Қаттылық', value: '19 МПа' },
          { label: 'Иілу беріктігі', value: '20 МПа' },
          { label: 'Дыбыс оқшаулау индексі', value: '27-31 дБ' },
          { label: 'Бұранданы суыру кедергісі', value: '35-40 Н/мм' },
          { label: 'Сызықтық кеңею коэффициенті', value: '0-0,02%' },
          { label: 'Өрт қауіптілігі класы', value: 'КМ1, Г1' }
        ],
        applications: ['Биік ғимараттар', 'Қасбеттер', 'Таза бөлмелер', 'Зертханалар']
      },
      en: {
        title: 'Fiber Cement Based',
        fullTitle: 'GIPSOVINIL.KZ Panels based on fiber cement board with PVC coating',
        description: 'Analog of SML board by technical characteristics with sheet height up to 3m. Maximum strength and durability for the most demanding projects.',
        features: ['Durability', 'Longevity', 'Moisture resistance', 'Height up to 3m'],
        keySpecs: [
          { label: 'Dimensions', value: '8×1200×3000mm', icon: 'maximize' },
          { label: 'Density', value: '1600 kg/m³', icon: 'layers' },
          { label: 'Fire resistance', value: 'KM1, G1', icon: 'flame' },
          { label: 'Moisture resistance', value: '3%', icon: 'droplets' },
          { label: 'Sound insulation', value: '27-31 dB', icon: 'volume' },
          { label: 'Weight', value: '13 kg/m²', icon: 'weight' }
        ],
        detailedSpecs: [
          { label: 'Description', value: 'Analog of SML board by technical characteristics, with sheet height of 3m' },
          { label: 'Panel composition', value: 'Fiber cement board (cement, sand, cellulose fibers)' },
          { label: 'Dimensions', value: '8x1200x3000mm' },
          { label: 'Density', value: '1600 kg/m³' },
          { label: 'Weight', value: '13 kg/m²' },
          { label: 'Release humidity', value: '3%' },
          { label: 'Hardness', value: '19 MPa' },
          { label: 'Bending strength', value: '20 MPa' },
          { label: 'Sound insulation index', value: '27-31 dB' },
          { label: 'Screw pull-out resistance', value: '35-40 N/mm' },
          { label: 'Linear expansion coefficient', value: '0-0.02%' },
          { label: 'Fire hazard class', value: 'KM1, G1' }
        ],
        applications: ['High-rise buildings', 'Facades', 'Clean rooms', 'Laboratories']
      },
      ar: {
        title: 'مستندة إلى الإسمنت الليفي',
        fullTitle: 'ألواح GIPSOVINIL.KZ المستندة إلى لوح الإسمنت الليفي مع طلاء PVC',
        description: 'بديل لوح SML من حيث الخصائص التقنية مع ارتفاع لوح يصل إلى 3 م. أقصى درجات المتانة والصلابة للمشاريع عالية المتطلبات.',
        features: ['المتانة', 'طول العمر الافتراضي', 'مقاومة الرطوبة', 'ارتفاع يصل إلى 3 م'],
        keySpecs: [
          { label: 'الأبعاد', value: '8×1200×3000 مم', icon: 'maximize' },
          { label: 'الكثافة', value: '1600 كجم/م³', icon: 'layers' },
          { label: 'مقاومة الحريق', value: 'KM1, G1', icon: 'flame' },
          { label: 'مقاومة الرطوبة', value: '3%', icon: 'droplets' },
          { label: 'العزل الصوتي', value: '27-31 ديسيبل', icon: 'volume' },
          { label: 'الوزن', value: '13 كجم/م²', icon: 'weight' }
        ],
        detailedSpecs: [
          { label: 'الوصف', value: 'بديل لوح SML من حيث الخصائص التقنية، بارتفاع لوح يبلغ 3 م' },
          { label: 'تركيبة اللوح', value: 'لوح إسمنت ليفي (إسمنت، رمل، ألياف سيليولوز)' },
          { label: 'الأبعاد', value: '8×1200×3000 مم' },
          { label: 'الكثافة', value: '1600 كجم/م³' },
          { label: 'الوزن', value: '13 كجم/م²' },
          { label: 'رطوبة الإصدار', value: '3%' },
          { label: 'الصلابة', value: '19 ميغاباسكال' },
          { label: 'مقاومة الانحناء', value: '20 ميغاباسكال' },
          { label: 'معامل العزل الصوتي', value: '27-31 ديسيبل' },
          { label: 'مقاومة سحب البراغي', value: '35-40 ن/مم' },
          { label: 'معامل التمدد الخطي', value: '0-0.02%' },
          { label: 'فئة خطر الحريق', value: 'KM1, G1' }
        ],
        applications: ['المباني الشاهقة', 'الواجهات الخارجية', 'الغرف النظيفة', 'المختبرات']
      }
    }
  };

  const labels = {
    ru: {
      back: 'Назад к продукции',
      techSpecs: 'Технические характеристики',
      keySpecs: 'Ключевые параметры',
      applications: 'Области применения',
      contact: 'Связаться с нами',
      catalog: 'Смотреть каталог текстур'
    },
    kz: {
      back: 'Өнімдерге оралу',
      techSpecs: 'Техникалық сипаттамалар',
      keySpecs: 'Негізгі параметрлер',
      applications: 'Қолдану салалары',
      contact: 'Бізбен байланысу',
      catalog: 'Текстуралар каталогын көру'
    },
    en: {
      back: 'Back to products',
      techSpecs: 'Technical Specifications',
      keySpecs: 'Key Parameters',
      applications: 'Applications',
      contact: 'Contact Us',
      catalog: 'View Texture Catalog'
    },
    ar: {
      back: 'العودة إلى المنتجات',
      techSpecs: 'المواصفات التقنية',
      keySpecs: 'المعاملات الرئيسية',
      applications: 'مجالات التطبيق',
      contact: 'تواصل معنا',
      catalog: 'استعراض كتالوج القوام'
    }
  };

  // SEO Content translations
  const seoContent = {
    gkl: {
      ru: {
        title: 'Панели GIPSOVINIL.KZ — современное решение для отделки стен',
        desc1: '<strong>Панели GIPSOVINIL.KZ</strong> — <strong>гипсовиниловые панели на основе прочного гипсокартона, покрытого декоративным ПВХ покрытием</strong>, который обеспечивает долговечность, износостойкость и привлекательный внешний вид.',
        desc2: 'Благодаря заводскому ламинированию ПВХ-плёнкой панели полностью готовы к эксплуатации сразу после монтажа, без необходимости шпаклёвки, покраски или дополнительной отделки. <strong>Гипсовиниловые панели также известны как ламинированный гипсокартон (ЛГКЛ) и применяются в коммерческих, медицинских, административных и жилых помещениях.</strong>',
        advantagesTitle: 'Основные преимущества гипсовиниловых панелей',
        advantages: [
          { title: 'Готовая чистовая поверхность', desc: 'Не требует шпаклёвки, покраски или дополнительной отделки — экономия времени и средств на ремонте.' },
          { title: 'Долговечность', desc: 'Износостойкое ПВХ покрытие защищает от царапин, потёртостей и сохраняет первоначальный вид на долгие годы.' },
          { title: 'Пожаробезопасность', desc: 'Класс пожарной опасности КМ2 — подходит для общественных и коммерческих помещений.' },
          { title: 'Влагостойкость', desc: 'Моющееся ПВХ покрытие позволяет легко очищать поверхность и использовать панели в помещениях с умеренной влажностью.' },
          { title: 'Экологичность', desc: 'Гипсовая основа и ПВХ покрытие безопасны для здоровья, не выделяют вредных веществ.' },
          { title: 'Эстетичность', desc: 'Широкий выбор текстур и оттенков — от классических однотонных до имитации дерева, камня и бетона.' }
        ],
        applicationsTitle: 'Сферы применения',
        applications: [
          { text: 'Офисные помещения', image: '/images/gkl_office.webp' },
          { text: 'Медицинские учреждения', image: '/images/gkl_medical.webp' },
          { text: 'Кабинеты директоров', image: '/images/gkl_director.webp' },
          { text: 'Административные здания', image: '/images/gkl_admin_building.webp' },
          { text: 'Переговорные', image: '/images/gkl_meeting.webp' },
          { text: 'Образовательные учреждения', image: '/images/gkl_education.webp' },
          { text: 'Гостиницы и отели', image: '/images/gkl_hotel.webp' },
          { text: 'Перегородки, коридоры', image: '/images/gkl_partitions.webp' },
          { text: 'Административно-бытовые корпусы (АБК)', image: '/images/gkl_abk.webp' },
          { text: 'Вахтовые городки', image: '/images/gkl_camp.webp' },
          { text: 'Общежития, хостелы', image: '/images/gkl_dormitory.webp' },
          { text: 'Раздевалки, подсобные помещения' },
          { text: 'Рестораны, кафе, магазины', image: '/images/gkl_restaurant.webp' },
          { text: 'Актовые залы', image: '/images/gkl_auditorium.webp' }
        ],
        whyChooseText: 'Наши панели сочетают в себе практичность и эстетичность: это готовое решение "под ключ", позволяющее сократить время отделочных работ и повысить долговечность поверхностей. Панели с декоративным ПВХ-покрытием сохраняют аккуратный вид, легко моются и служат долгие годы даже при интенсивной эксплуатации.'
      },
      kz: {
        title: 'GIPSOVINIL.KZ панельдері — қабырғаларды әрлеудің заманауи шешімі',
        desc1: '<strong>GIPSOVINIL.KZ панельдері</strong> — <strong>декоративті ПВХ жабынымен жабылған берік гипсокартон негізіндегі гипсовинил панельдер</strong>, ұзақ мерзімділікті, тозуға төзімділікті және тартымды сыртқы түрді қамтамасыз етеді.',
        desc2: 'Зауыттық ПВХ-пленкамен ламинациялау арқасында панельдер монтаждан кейін бірден пайдалануға дайын, шпаклёвка, бояу немесе қосымша әрлеу қажет емес. <strong>Гипсовинил панельдер ламинатталған гипсокартон (ЛГКЛ) деп те аталады және коммерциялық, медициналық, әкімшілік және тұрғын үй-жайларда қолданылады.</strong>',
        advantagesTitle: 'Гипсовинил панельдердің негізгі артықшылықтары',
        advantages: [
          { title: 'Дайын таза беті', desc: 'Шпаклёвка, бояу немесе қосымша әрлеу қажет емес — жөндеуге уақыт пен қаражатты үнемдейді.' },
          { title: 'Ұзақ мерзімділік', desc: 'Тозуға төзімді ПВХ жабыны сызаттар мен тозудан қорғайды және көп жылдар бойы бастапқы көрінісін сақтайды.' },
          { title: 'Өрт қауіпсіздігі', desc: 'КМ2 өрт қауіптілігі класы — қоғамдық және коммерциялық үй-жайларға жарамды.' },
          { title: 'Ылғалға төзімділік', desc: 'Жуылатын ПВХ жабыны бетті оңай тазартуға және орташа ылғалдылықтағы бөлмелерде панельдерді пайдалануға мүмкіндік береді.' },
          { title: 'Экологиялық таза', desc: 'Гипс негізі мен ПВХ жабыны денсаулыққа қауіпсіз, зиянды заттар бөлмейді.' },
          { title: 'Эстетикалық', desc: 'Текстуралар мен реңктердің кең таңдауы — классикалық біртекті түстерден ағаш, тас және бетон имитациясына дейін.' }
        ],
        applicationsTitle: 'Қолдану салалары',
        applications: [
          { text: 'Кеңсе үй-жайлары', image: '/images/gkl_office.webp' },
          { text: 'Медициналық мекемелер', image: '/images/gkl_medical.webp' },
          { text: 'Директор кабинеттері', image: '/images/gkl_director.webp' },
          { text: 'Әкімшілік ғимараттар', image: '/images/gkl_admin_building.webp' },
          { text: 'Келіссөз бөлмелері', image: '/images/gkl_meeting.webp' },
          { text: 'Білім беру мекемелері', image: '/images/gkl_education.webp' },
          { text: 'Қонақ үйлер', image: '/images/gkl_hotel.webp' },
          { text: 'Аралық қабырғалар, дәліздер', image: '/images/gkl_partitions.webp' },
          { text: 'Әкімшілік-тұрмыстық корпустар (ӘТК)', image: '/images/gkl_abk.webp' },
          { text: 'Вахталық қалашықтар', image: '/images/gkl_camp.webp' },
          { text: 'Жатақханалар, хостелдер', image: '/images/gkl_dormitory.webp' },
          { text: 'Киім ауыстыру, қосалқы бөлмелер' },
          { text: 'Мейрамханалар, кафелер, дүкендер', image: '/images/gkl_restaurant.webp' },
          { text: 'Мәжіліс залдары', image: '/images/gkl_auditorium.webp' }
        ],
        whyChooseText: 'Біздің панельдер практикалық және эстетикалықты үйлестіреді: бұл "кілт тапсыру" дайын шешім, әрлеу жұмыстарының уақытын қысқартуға және беттердің ұзақ мерзімділігін арттыруға мүмкіндік береді. Декоративті ПВХ жабыны бар панельдер ұқыпты көрінісін сақтайды, оңай жуылады және интенсивті пайдалану кезінде де көп жылдар қызмет етеді.'
      },
      en: {
        title: 'GIPSOVINIL.KZ Panels — Modern Wall Finishing Solution',
        desc1: '<strong>GIPSOVINIL.KZ Panels</strong> — <strong>gypsum vinyl panels based on durable drywall covered with decorative PVC coating</strong>, which ensures durability, wear resistance and attractive appearance.',
        desc2: 'Thanks to factory PVC film lamination, panels are ready for use immediately after installation, without the need for putty, painting or additional finishing. <strong>Gypsum vinyl panels are also known as laminated drywall (LGKL) and are used in commercial, medical, administrative and residential premises.</strong>',
        advantagesTitle: 'Key Advantages of Gypsum Vinyl Panels',
        advantages: [
          { title: 'Ready-made finish surface', desc: 'No putty, painting or additional finishing required — saves time and money on renovation.' },
          { title: 'Durability', desc: 'Wear-resistant PVC coating protects against scratches and scuffs, maintaining original appearance for years.' },
          { title: 'Fire safety', desc: 'KM2 fire hazard class — suitable for public and commercial premises.' },
          { title: 'Moisture resistance', desc: 'Washable PVC coating allows easy surface cleaning and use in rooms with moderate humidity.' },
          { title: 'Eco-friendly', desc: 'Gypsum base and PVC coating are safe for health, do not emit harmful substances.' },
          { title: 'Aesthetics', desc: 'Wide selection of textures and shades — from classic solid colors to wood, stone and concrete imitation.' }
        ],
        applicationsTitle: 'Applications',
        applications: [
          { text: 'Office premises', image: '/images/gkl_office.webp' },
          { text: 'Medical facilities', image: '/images/gkl_medical.webp' },
          { text: 'Executive offices', image: '/images/gkl_director.webp' },
          { text: 'Administrative buildings', image: '/images/gkl_admin_building.webp' },
          { text: 'Meeting rooms', image: '/images/gkl_meeting.webp' },
          { text: 'Educational institutions', image: '/images/gkl_education.webp' },
          { text: 'Hotels', image: '/images/gkl_hotel.webp' },
          { text: 'Partitions, corridors', image: '/images/gkl_partitions.webp' },
          { text: 'Administrative buildings (ABK)', image: '/images/gkl_abk.webp' },
          { text: 'Rotational camps', image: '/images/gkl_camp.webp' },
          { text: 'Dormitories, hostels', image: '/images/gkl_dormitory.webp' },
          { text: 'Locker rooms, utility rooms' },
          { text: 'Restaurants, cafes, shops', image: '/images/gkl_restaurant.webp' },
          { text: 'Auditoriums', image: '/images/gkl_auditorium.webp' }
        ],
        whyChooseText: 'Our panels combine practicality and aesthetics: this is a turnkey solution that reduces finishing time and increases surface durability. Panels with decorative PVC coating maintain a neat appearance, are easy to clean and last for years even under intensive use.'
      },
      ar: {
        title: 'ألواح GIPSOVINIL.KZ — الحل العصري لتشطيب الجدران',
        desc1: '<strong>ألواح GIPSOVINIL.KZ</strong> — <strong>ألواح جبسية فينيلية مستندة إلى ألواح جبس متينة مغطاة بطلاء PVC زخرفي</strong>، يضمن المتانة ومقاومة التآكل والمظهر الجذاب.',
        desc2: 'بفضل الترقيق المصنعي بالغشاء PVC، تكون الألواح جاهزة للاستخدام فور التركيب، دون الحاجة إلى معجون أو طلاء أو تشطيب إضافي. <strong>تُعرف ألواح الجبس الفينيلية أيضاً بألواح الجبس المصفّح (LGKL)، وتُستخدم في المنشآت التجارية والطبية والإدارية والسكنية.</strong>',
        advantagesTitle: 'المزايا الرئيسية للألواح الجبسية الفينيلية',
        advantages: [
          { title: 'سطح تشطيب جاهز', desc: 'لا يتطلب معجوناً أو طلاءً أو تشطيباً إضافياً — يوفّر الوقت والمال في أعمال التجديد.' },
          { title: 'المتانة', desc: 'طلاء PVC المقاوم للتآكل يحمي من الخدوش والسحجات، محافظاً على المظهر الأصلي لسنوات.' },
          { title: 'السلامة من الحريق', desc: 'تصنيف خطر الحريق KM2 — مناسب للمنشآت العامة والتجارية.' },
          { title: 'مقاومة الرطوبة', desc: 'يتيح طلاء PVC القابل للغسل تنظيف الأسطح بسهولة واستخدامها في الغرف ذات الرطوبة المعتدلة.' },
          { title: 'صديقة للبيئة', desc: 'القاعدة الجبسية وطلاء PVC آمنان للصحة ولا يُطلقان مواد ضارة.' },
          { title: 'الجماليات', desc: 'مجموعة واسعة من القوام والألوان — من الألوان الصلبة الكلاسيكية إلى محاكاة الخشب والحجر والخرسانة.' }
        ],
        applicationsTitle: 'مجالات التطبيق',
        applications: [
          { text: 'المكاتب والمساحات الإدارية', image: '/images/gkl_office.webp' },
          { text: 'المنشآت الطبية', image: '/images/gkl_medical.webp' },
          { text: 'مكاتب المديرين التنفيذيين', image: '/images/gkl_director.webp' },
          { text: 'المباني الإدارية', image: '/images/gkl_admin_building.webp' },
          { text: 'قاعات الاجتماعات', image: '/images/gkl_meeting.webp' },
          { text: 'المؤسسات التعليمية', image: '/images/gkl_education.webp' },
          { text: 'الفنادق', image: '/images/gkl_hotel.webp' },
          { text: 'الحواجز والممرات', image: '/images/gkl_partitions.webp' },
          { text: 'المباني الإدارية (ABK)', image: '/images/gkl_abk.webp' },
          { text: 'مخيمات العمال الدورية', image: '/images/gkl_camp.webp' },
          { text: 'المساكن الجماعية والنزل', image: '/images/gkl_dormitory.webp' },
          { text: 'غرف تبديل الملابس والغرف الخدمية' },
          { text: 'المطاعم والمقاهي والمحلات التجارية', image: '/images/gkl_restaurant.webp' },
          { text: 'القاعات الدراسية والمحاضرات', image: '/images/gkl_auditorium.webp' }
        ],
        whyChooseText: 'تجمع ألواحنا بين العملية والجمالية: فهي حل متكامل جاهز يقلّص وقت التشطيب ويرفع من متانة الأسطح. تحافظ الألواح ذات الطلاء الزخرفي من PVC على مظهرها الأنيق، وسهلة التنظيف، وتدوم لسنوات طويلة حتى في ظروف الاستخدام المكثّف.'
      }
    },
    comfort: {
      ru: {
        title: 'Панели Комфорт — усиленное решение для требовательных объектов',
        desc1: '<strong>Панели Комфорт GIPSOVINIL.KZ</strong> — <strong>влагостойкие и пожаробезопасные гипсовиниловые панели на основе гипсокартона, армированного стекловолокном</strong>, с износостойким ПВХ покрытием. Идеальное решение для объектов с высокими требованиями к прочности и безопасности.',
        desc2: 'Благодаря армированию стекловолокном панели обладают повышенной ударопрочностью и устойчивостью к механическим повреждениям. <strong>Панели Комфорт на основе гипсокартона со стекловолокном идеально подходят для жилых комплексов: внеквартирных коридоров, лобби, подъездов, входных групп, спортзалов, актовых залов, школ, медицинских учреждений, торговых центров, гостиниц, помещений с высокой проходимостью, а также для влажных зон: бассейнов, санузлов, умывальных зон.</strong>',
        advantagesTitle: 'Основные преимущества панелей Комфорт',
        advantages: [
          { title: 'Повышенная ударопрочность', desc: 'Армирование стекловолокном обеспечивает высокую устойчивость к механическим повреждениям и ударам.', icon: 'shield' },
          { title: 'Пожаробезопасность КМ1', desc: 'Класс пожарной опасности КМ1, Г1 — подходит для объектов с повышенными требованиями к пожарной безопасности.', icon: 'flame' },
          { title: 'Влагостойкость', desc: 'Влагостойкий картон и ПВХ-покрытие позволяют использовать панели в помещениях с влажностью до 65%.', icon: 'droplets' },
          { title: 'Быстрый монтаж', desc: 'Готовая чистовая поверхность — не требует шпаклёвки и покраски, что значительно сокращает сроки отделки.', icon: 'wrench' },
          { title: 'Гигиеничность', desc: 'ПВХ-поверхность легко моется и дезинфицируется — идеально для медицинских и детских учреждений.', icon: 'check' },
          { title: 'Высокая плотность', desc: 'Плотность 1200 кг/м³ обеспечивает отличную звукоизоляцию и долговечность конструкции.', icon: 'layers' }
        ],
        applicationsTitle: 'Сферы применения',
        applications: [
          { text: 'Медицинские учреждения и клиники', image: '/images/comfort_medical.webp' },
          { text: 'Учебные заведения', image: '/images/comfort_education.webp' },
          { text: 'Офисные помещения', image: '/images/comfort_office.webp' },
          { text: 'Жилые комплексы и подъезды', image: '/images/comfort_lobby.webp' },
          { text: 'Бассейны и помещения с высокой влажностью', image: '/images/comfort_pool.webp' },
          { text: 'Спортивные комплексы', image: '/images/comfort_sports.webp' },
          { text: 'Лобби и коридоры', image: '/images/comfort_lobby_corridor.webp' },
          { text: 'Торговые и бизнес-центры', image: '/images/comfort_business.webp' },
          { text: 'Рестораны и кафе', image: '/images/comfort_restaurant.webp' },
          { text: 'Гостиницы и отели', image: '/images/comfort_hotel.webp' },
          { text: 'Столовые' },
          { text: 'Актовые залы' },
          { text: 'Супермаркеты' }
        ],
        whyChooseTitle: 'Почему выбирают панели GIPSOVINIL.KZ?',
        whyChooseText: 'Наши панели сочетают в себе практичность и эстетичность: это готовое решение "под ключ", позволяющее сократить время отделочных работ и повысить долговечность поверхностей. Панели с декоративным ПВХ-покрытием сохраняют аккуратный вид, легко моются и служат долгие годы даже при интенсивной эксплуатации.'
      },
      kz: {
        title: 'Комфорт панельдері — талапшыл объектілер үшін күшейтілген шешім',
        desc1: '<strong>Комфорт GIPSOVINIL.KZ панельдері</strong> — <strong>шыны талшықпен армирленген гипсокартон негізіндегі ылғалға төзімді және өртке қауіпсіз гипсовинил панельдер</strong>, тозуға төзімді ПВХ жабынымен. Беріктік пен қауіпсіздікке жоғары талаптар қойылатын объектілер үшін тамаша шешім.',
        desc2: 'Шыны талшықпен армирлеу арқасында панельдер жоғары соққыға төзімділік пен механикалық зақымдануларға төзімділікке ие. <strong>Шыны талшықты гипсокартон негізіндегі Комфорт панельдері тұрғын үй кешендері үшін өте қолайлы: пәтерден тыс дәліздер, лобби, кіреберіс, кіру топтары, спорт залдары, мәжіліс залдары, мектептер, медициналық мекемелер, сауда орталықтары, қонақ үйлер, жоғары өткізу қабілеті бар бөлмелер, сонымен қатар ылғалды аймақтар: бассейндер, дәретханалар, жуынатын аймақтар.</strong>',
        advantagesTitle: 'Комфорт панельдерінің негізгі артықшылықтары',
        advantages: [
          { title: 'Жоғары соққыға төзімділік', desc: 'Шыны талшықпен армирлеу механикалық зақымдануларға және соққыларға жоғары төзімділікті қамтамасыз етеді.', icon: 'shield' },
          { title: 'Өрт қауіпсіздігі КМ1', desc: 'КМ1, Г1 өрт қауіптілігі класы — өрт қауіпсіздігіне жоғары талаптар қойылатын объектілерге жарамды.', icon: 'flame' },
          { title: 'Ылғалға төзімділік', desc: 'Ылғалға төзімді картон мен ПВХ жабыны 65%-ға дейін ылғалдылықтағы бөлмелерде панельдерді пайдалануға мүмкіндік береді.', icon: 'droplets' },
          { title: 'Жылдам монтаж', desc: 'Дайын таза беті — шпаклёвка мен бояу қажет емес, бұл әрлеу мерзімін айтарлықтай қысқартады.', icon: 'wrench' },
          { title: 'Гигиеналық', desc: 'ПВХ беті оңай жуылады және дезинфекцияланады — медициналық және балалар мекемелері үшін тамаша.', icon: 'check' },
          { title: 'Жоғары тығыздық', desc: '1200 кг/м³ тығыздық тамаша дыбыс оқшаулауды және құрылымның ұзақ мерзімділігін қамтамасыз етеді.', icon: 'layers' }
        ],
        applicationsTitle: 'Қолдану салалары',
        applications: [
          { text: 'Медициналық мекемелер мен клиникалар', image: '/images/comfort_medical.webp' },
          { text: 'Оқу орындары', image: '/images/comfort_education.webp' },
          { text: 'Кеңсе үй-жайлары', image: '/images/comfort_office.webp' },
          { text: 'Тұрғын үй кешендері мен кіреберістер', image: '/images/comfort_lobby.webp' },
          { text: 'Бассейндер және жоғары ылғалдылықтағы бөлмелер', image: '/images/comfort_pool.webp' },
          { text: 'Спорт кешендері', image: '/images/comfort_sports.webp' },
          { text: 'Лобби және дәліздер', image: '/images/comfort_lobby_corridor.webp' },
          { text: 'Сауда және бизнес орталықтар', image: '/images/comfort_business.webp' },
          { text: 'Мейрамханалар мен кафелер', image: '/images/comfort_restaurant.webp' },
          { text: 'Қонақ үйлер', image: '/images/comfort_hotel.webp' },
          { text: 'Асханалар' },
          { text: 'Мәжіліс залдары' },
          { text: 'Супермаркеттер' }
        ],
        whyChooseTitle: 'Неге GIPSOVINIL.KZ панельдерін таңдайды?',
        whyChooseText: 'Біздің панельдер практикалық және эстетикалықты үйлестіреді: бұл "кілт тапсыру" дайын шешім, әрлеу жұмыстарының уақытын қысқартуға және беттердің ұзақ мерзімділігін арттыруға мүмкіндік береді. Декоративті ПВХ жабыны бар панельдер ұқыпты көрінісін сақтайды, оңай жуылады және интенсивті пайдалану кезінде де көп жылдар қызмет етеді.'
      },
      en: {
        title: 'Comfort Panels — Enhanced Solution for Demanding Facilities',
        desc1: '<strong>Comfort GIPSOVINIL.KZ Panels</strong> — <strong>moisture-resistant and fire-safe gypsum vinyl panels based on fiberglass-reinforced drywall</strong> with wear-resistant PVC coating. Ideal solution for facilities with high strength and safety requirements.',
        desc2: 'Thanks to fiberglass reinforcement, panels have increased impact resistance and resistance to mechanical damage. <strong>Comfort panels based on fiberglass drywall are ideal for residential complexes: non-apartment corridors, lobbies, entrances, entrance groups, gyms, assembly halls, schools, medical facilities, shopping centers, hotels, high-traffic areas, as well as wet areas: pools, bathrooms, washing areas.</strong>',
        advantagesTitle: 'Key Advantages of Comfort Panels',
        advantages: [
          { title: 'Enhanced impact resistance', desc: 'Fiberglass reinforcement provides high resistance to mechanical damage and impacts.', icon: 'shield' },
          { title: 'Fire safety KM1', desc: 'KM1, G1 fire hazard class — suitable for facilities with high fire safety requirements.', icon: 'flame' },
          { title: 'Moisture resistance', desc: 'Moisture-resistant cardboard and PVC coating allow panels to be used in rooms with humidity up to 65%.', icon: 'droplets' },
          { title: 'Fast installation', desc: 'Ready-made finish surface — no putty or painting required, significantly reducing finishing time.', icon: 'wrench' },
          { title: 'Hygiene', desc: 'PVC surface is easy to wash and disinfect — ideal for medical and children\'s facilities.', icon: 'check' },
          { title: 'High density', desc: '1200 kg/m³ density ensures excellent sound insulation and structural durability.', icon: 'layers' }
        ],
        applicationsTitle: 'Applications',
        applications: [
          { text: 'Medical facilities and clinics', image: '/images/comfort_medical.webp' },
          { text: 'Educational institutions', image: '/images/comfort_education.webp' },
          { text: 'Office premises', image: '/images/comfort_office.webp' },
          { text: 'Residential complexes and entrances', image: '/images/comfort_lobby.webp' },
          { text: 'Pools and high humidity areas', image: '/images/comfort_pool.webp' },
          { text: 'Sports complexes', image: '/images/comfort_sports.webp' },
          { text: 'Lobbies and corridors', image: '/images/comfort_lobby_corridor.webp' },
          { text: 'Shopping and business centers', image: '/images/comfort_business.webp' },
          { text: 'Restaurants and cafes', image: '/images/comfort_restaurant.webp' },
          { text: 'Hotels', image: '/images/comfort_hotel.webp' },
          { text: 'Canteens' },
          { text: 'Assembly halls' },
          { text: 'Supermarkets' }
        ],
        whyChooseTitle: 'Why Choose GIPSOVINIL.KZ Panels?',
        whyChooseText: 'Our panels combine practicality and aesthetics: this is a turnkey solution that reduces finishing time and increases surface durability. Panels with decorative PVC coating maintain a neat appearance, are easy to clean and last for years even under intensive use.'
      },
      ar: {
        title: 'ألواح Comfort — الحل المتطور للمنشآت عالية المتطلبات',
        desc1: '<strong>ألواح Comfort من GIPSOVINIL.KZ</strong> — <strong>ألواح جبسية فينيلية مقاومة للرطوبة وآمنة من الحريق، مستندة إلى ألواح جبس مسلّحة بالألياف الزجاجية</strong> مع طلاء PVC مقاوم للتآكل. الحل الأمثل للمنشآت ذات متطلبات المتانة والسلامة العالية.',
        desc2: 'بفضل التسليح بالألياف الزجاجية، تتمتع الألواح بمقاومة متزايدة للصدمات والأضرار الميكانيكية. <strong>تُعدّ ألواح Comfort المستندة إلى الجبس الزجاجي الخيار الأمثل للمجمعات السكنية: ممرات الطوابق، والبهوّات، والمداخل، ومجموعات المدخل، وصالات الرياضة، وقاعات التجمع، والمدارس، والمنشآت الطبية، ومراكز التسوق، والفنادق، والمناطق عالية الحركة، فضلاً عن المناطق الرطبة: حمامات السباحة، والحمامات، ومناطق الغسيل.</strong>',
        advantagesTitle: 'المزايا الرئيسية لألواح Comfort',
        advantages: [
          { title: 'مقاومة صدمات معززة', desc: 'يوفّر التسليح بالألياف الزجاجية مقاومة عالية للأضرار الميكانيكية والصدمات.', icon: 'shield' },
          { title: 'السلامة من الحريق KM1', desc: 'تصنيف خطر الحريق KM1 وG1 — مناسبة للمنشآت ذات متطلبات السلامة من الحريق الصارمة.', icon: 'flame' },
          { title: 'مقاومة الرطوبة', desc: 'يتيح الكرتون المقاوم للرطوبة وطلاء PVC استخدام الألواح في الغرف ذات رطوبة تصل إلى 65%.', icon: 'droplets' },
          { title: 'تركيب سريع', desc: 'سطح تشطيب جاهز — لا يتطلب معجوناً أو طلاءً، مما يقلّص وقت التشطيب بشكل ملحوظ.', icon: 'wrench' },
          { title: 'النظافة الصحية', desc: 'سطح PVC سهل الغسل والتعقيم — مثالي للمنشآت الطبية ومرافق الأطفال.', icon: 'check' },
          { title: 'كثافة عالية', desc: 'كثافة 1200 كجم/م³ تضمن عزلاً صوتياً ممتازاً ومتانة هيكلية فائقة.', icon: 'layers' }
        ],
        applicationsTitle: 'مجالات التطبيق',
        applications: [
          { text: 'المنشآت الطبية والعيادات', image: '/images/comfort_medical.webp' },
          { text: 'المؤسسات التعليمية', image: '/images/comfort_education.webp' },
          { text: 'المكاتب والمساحات الإدارية', image: '/images/comfort_office.webp' },
          { text: 'المجمعات السكنية والمداخل', image: '/images/comfort_lobby.webp' },
          { text: 'حمامات السباحة ومناطق الرطوبة العالية', image: '/images/comfort_pool.webp' },
          { text: 'المجمعات الرياضية', image: '/images/comfort_sports.webp' },
          { text: 'البهوّات والممرات', image: '/images/comfort_lobby_corridor.webp' },
          { text: 'مراكز التسوق والأعمال', image: '/images/comfort_business.webp' },
          { text: 'المطاعم والمقاهي', image: '/images/comfort_restaurant.webp' },
          { text: 'الفنادق', image: '/images/comfort_hotel.webp' },
          { text: 'المطاعم الشعبية والكافتيريات' },
          { text: 'قاعات التجمع' },
          { text: 'الأسواق الكبرى' }
        ],
        whyChooseTitle: 'لماذا تختار ألواح GIPSOVINIL.KZ؟',
        whyChooseText: 'تجمع ألواحنا بين العملية والجمالية: فهي حل متكامل جاهز يقلّص وقت التشطيب ويرفع من متانة الأسطح. تحافظ الألواح ذات الطلاء الزخرفي من PVC على مظهرها الأنيق، وسهلة التنظيف، وتدوم لسنوات طويلة حتى في ظروف الاستخدام المكثّف.'
      }
    },
    sml: {
      ru: {
        title: 'Панели на основе СМЛ — максимальная огнестойкость и влагостойкость',
        desc1: '<strong>Панели GIPSOVINIL.KZ на основе стекломагниевого листа (СМЛ)</strong> — <strong>негорючие, экологически чистые и максимально влагостойкие панели</strong> с декоративным ПВХ покрытием. Идеальное решение для объектов с повышенными требованиями к пожарной безопасности.',
        desc2: 'Благодаря уникальному составу (оксид магния, хлорид магния, перлит, стеклоткань) панели обладают классом пожарной опасности КМ1, Г1 и выдерживают прямое воздействие огня. <strong>СМЛ панели идеально подходят для государственных учреждений, метро, аэропортов, промышленных объектов и помещений с особыми требованиями к пожарной безопасности.</strong>',
        advantagesTitle: 'Основные преимущества панелей на основе СМЛ',
        advantages: [
          { title: 'Пожаробезопасность КМ1 Г1', desc: 'Максимальный класс огнестойкости — панели выдерживают прямое воздействие огня и не поддерживают горение.', icon: 'flame' },
          { title: 'Максимальная влагостойкость', desc: 'Водопоглощение менее 5% — подходят для помещений с любым уровнем влажности, включая бассейны.', icon: 'droplets' },
          { title: 'Экологичность', desc: 'Натуральный состав без вредных веществ — безопасны для здоровья и окружающей среды.', icon: 'shield' },
          { title: 'Быстрый монтаж', desc: 'Готовая чистовая поверхность — не требует шпаклёвки и покраски, значительно сокращает сроки отделки.', icon: 'wrench' },
          { title: 'Отличная шумоизоляция', desc: 'Индекс шумоизоляции 35-40 дБ — эффективно снижает уровень шума в помещениях.', icon: 'volume' },
          { title: 'Высокая прочность', desc: 'Предел прочности при изгибе 7-12 МПа — выдерживает значительные механические нагрузки.', icon: 'layers' }
        ],
        applicationsTitle: 'Сферы применения',
        applications: [{ text: 'Государственные учреждения', image: '/images/sml_government.webp' }, 'Метро и аэропорты', 'Промышленные объекты', { text: 'Спорткомплексы и стадионы', image: '/images/sml_sports.webp' }, { text: 'Бассейны и аквапарки', image: '/images/sml_pool.webp' }, 'Склады и логистические центры', 'Электростанции и подстанции', 'Медицинские учреждения', 'Учебные заведения', { text: 'Паркинг', image: '/images/sml_parking.webp' }],
        whyChooseTitle: 'Почему выбирают панели GIPSOVINIL.KZ?',
        whyChooseText: 'Наши панели сочетают в себе практичность и эстетичность: это готовое решение "под ключ", позволяющее сократить время отделочных работ и повысить долговечность поверхностей. Панели с декоративным ПВХ-покрытием сохраняют аккуратный вид, легко моются и служат долгие годы даже при интенсивной эксплуатации.'
      },
      kz: {
        title: 'СМЛ негізіндегі панельдер — максималды өртке төзімділік және ылғалға төзімділік',
        desc1: '<strong>Шыны-магний парағы (СМЛ) негізіндегі GIPSOVINIL.KZ панельдері</strong> — <strong>жанбайтын, экологиялық таза және максималды ылғалға төзімді панельдер</strong> декоративті ПВХ жабынымен. Өрт қауіпсіздігіне жоғары талаптар қойылатын объектілер үшін тамаша шешім.',
        desc2: 'Бірегей құрамның (магний оксиді, магний хлориді, перлит, шыны мата) арқасында панельдер КМ1, Г1 өрт қауіптілігі класына ие және оттың тікелей әсеріне төтеп береді. <strong>СМЛ панельдері мемлекеттік мекемелер, метро, әуежайлар, өнеркәсіптік объектілер және өрт қауіпсіздігіне ерекше талаптар қойылатын бөлмелер үшін өте қолайлы.</strong>',
        advantagesTitle: 'СМЛ негізіндегі панельдердің негізгі артықшылықтары',
        advantages: [
          { title: 'Өрт қауіпсіздігі КМ1 Г1', desc: 'Максималды өртке төзімділік класы — панельдер оттың тікелей әсеріне төтеп береді және жануды қолдамайды.', icon: 'flame' },
          { title: 'Максималды ылғалға төзімділік', desc: 'Су сіңіру 5%-дан аз — бассейндерді қоса алғанда, кез келген ылғалдылық деңгейіндегі бөлмелерге жарамды.', icon: 'droplets' },
          { title: 'Экологиялық таза', desc: 'Зиянды заттарсыз табиғи құрам — денсаулық пен қоршаған ортаға қауіпсіз.', icon: 'shield' },
          { title: 'Жылдам монтаж', desc: 'Дайын таза беті — шпаклёвка мен бояу қажет емес, әрлеу мерзімін айтарлықтай қысқартады.', icon: 'wrench' },
          { title: 'Тамаша дыбыс оқшаулау', desc: '35-40 дБ дыбыс оқшаулау индексі — бөлмелердегі шу деңгейін тиімді төмендетеді.', icon: 'volume' },
          { title: 'Жоғары беріктік', desc: 'Иілу беріктігінің шегі 7-12 МПа — айтарлықтай механикалық жүктемелерге төтеп береді.', icon: 'layers' }
        ],
        applicationsTitle: 'Қолдану салалары',
        applications: [{ text: 'Мемлекеттік мекемелер', image: '/images/sml_government.webp' }, 'Метро және әуежайлар', 'Өнеркәсіптік объектілер', { text: 'Спорт кешендері мен стадиондар', image: '/images/sml_sports.webp' }, { text: 'Бассейндер және аквапарктер', image: '/images/sml_pool.webp' }, 'Қоймалар және логистикалық орталықтар', 'Электр станциялары және қосалқы станциялар', 'Медициналық мекемелер', 'Оқу орындары', { text: 'Паркинг', image: '/images/sml_parking.webp' }],
        whyChooseTitle: 'Неге GIPSOVINIL.KZ панельдерін таңдайды?',
        whyChooseText: 'Біздің панельдер практикалық және эстетикалықты үйлестіреді: бұл "кілт тапсыру" дайын шешім, әрлеу жұмыстарының уақытын қысқартуға және беттердің ұзақ мерзімділігін арттыруға мүмкіндік береді. Декоративті ПВХ жабыны бар панельдер ұқыпты көрінісін сақтайды, оңай жуылады және интенсивті пайдалану кезінде де көп жылдар қызмет етеді.'
      },
      en: {
        title: 'SML Based Panels — Maximum Fire and Moisture Resistance',
        desc1: '<strong>GIPSOVINIL.KZ Panels based on glass-magnesium sheet (SML)</strong> — <strong>non-combustible, eco-friendly and maximally moisture-resistant panels</strong> with decorative PVC coating. Ideal solution for facilities with high fire safety requirements.',
        desc2: 'Thanks to unique composition (magnesium oxide, magnesium chloride, perlite, fiberglass cloth) panels have KM1, G1 fire hazard class and withstand direct fire exposure. <strong>SML panels are ideal for government facilities, metro, airports, industrial facilities and premises with special fire safety requirements.</strong>',
        advantagesTitle: 'Key Advantages of SML Based Panels',
        advantages: [
          { title: 'Fire safety KM1 G1', desc: 'Maximum fire resistance class — panels withstand direct fire exposure and do not support combustion.', icon: 'flame' },
          { title: 'Maximum moisture resistance', desc: 'Water absorption less than 5% — suitable for rooms with any humidity level, including pools.', icon: 'droplets' },
          { title: 'Eco-friendly', desc: 'Natural composition without harmful substances — safe for health and environment.', icon: 'shield' },
          { title: 'Fast installation', desc: 'Ready-made finish surface — no putty or painting required, significantly reducing finishing time.', icon: 'wrench' },
          { title: 'Excellent sound insulation', desc: '35-40 dB sound insulation index — effectively reduces noise levels in rooms.', icon: 'volume' },
          { title: 'High strength', desc: 'Bending strength limit 7-12 MPa — withstands significant mechanical loads.', icon: 'layers' }
        ],
        applicationsTitle: 'Applications',
        applications: [{ text: 'Government facilities', image: '/images/sml_government.webp' }, 'Metro and airports', 'Industrial facilities', { text: 'Sports complexes and stadiums', image: '/images/sml_sports.webp' }, { text: 'Pools and water parks', image: '/images/sml_pool.webp' }, 'Warehouses and logistics centers', 'Power plants and substations', 'Medical facilities', 'Educational institutions', { text: 'Parking', image: '/images/sml_parking.webp' }],
        whyChooseTitle: 'Why Choose GIPSOVINIL.KZ Panels?',
        whyChooseText: 'Our panels combine practicality and aesthetics: this is a turnkey solution that reduces finishing time and increases surface durability. Panels with decorative PVC coating maintain a neat appearance, are easy to clean and last for years even under intensive use.'
      },
      ar: {
        title: 'ألواح SML — أقصى درجات مقاومة الحريق والرطوبة',
        desc1: '<strong>ألواح GIPSOVINIL.KZ المستندة إلى الصفيحة الزجاجية المغنيسيومية (SML)</strong> — <strong>ألواح غير قابلة للاحتراق، صديقة للبيئة، وعالية المقاومة للرطوبة</strong> مع طلاء PVC زخرفي. الحل الأمثل للمنشآت ذات متطلبات السلامة من الحريق الصارمة.',
        desc2: 'بفضل تركيبتها الفريدة (أكسيد المغنيسيوم، كلوريد المغنيسيوم، البيرلايت، قماش الألياف الزجاجية)، تحمل الألواح تصنيف خطر الحريق KM1 وG1 وتتحمل التعرض المباشر للهب. <strong>تُعدّ ألواح SML الخيار الأمثل للمنشآت الحكومية، والمترو، والمطارات، والمنشآت الصناعية، والأماكن ذات اشتراطات خاصة بالسلامة من الحريق.</strong>',
        advantagesTitle: 'المزايا الرئيسية لألواح SML',
        advantages: [
          { title: 'السلامة من الحريق KM1 G1', desc: 'أعلى درجة لمقاومة الحريق — تتحمل الألواح التعرض المباشر للهب ولا تدعم الاشتعال.', icon: 'flame' },
          { title: 'أقصى درجات مقاومة الرطوبة', desc: 'امتصاص مائي أقل من 5% — مناسبة للغرف ذات أي مستوى رطوبة، بما في ذلك حمامات السباحة.', icon: 'droplets' },
          { title: 'صديقة للبيئة', desc: 'تركيبة طبيعية خالية من المواد الضارة — آمنة للصحة والبيئة.', icon: 'shield' },
          { title: 'تركيب سريع', desc: 'سطح تشطيب جاهز — لا يتطلب معجوناً أو طلاءً، مما يقلّص وقت التشطيب بشكل ملحوظ.', icon: 'wrench' },
          { title: 'عزل صوتي ممتاز', desc: 'معامل العزل الصوتي 35-40 ديسيبل — يخفّض مستويات الضوضاء بفعالية في الغرف.', icon: 'volume' },
          { title: 'متانة عالية', desc: 'حد مقاومة الانحناء 7-12 ميغاباسكال — تتحمل أحمالاً ميكانيكية جسيمة.', icon: 'layers' }
        ],
        applicationsTitle: 'مجالات التطبيق',
        applications: [{ text: 'المنشآت الحكومية', image: '/images/sml_government.webp' }, 'المترو والمطارات', 'المنشآت الصناعية', { text: 'المجمعات الرياضية والملاعب', image: '/images/sml_sports.webp' }, { text: 'حمامات السباحة والمدن المائية', image: '/images/sml_pool.webp' }, 'المستودعات ومراكز اللوجستيك', 'محطات الطاقة ومحطات التحويل الكهربائية', 'المنشآت الطبية', 'المؤسسات التعليمية', { text: 'مواقف السيارات', image: '/images/sml_parking.webp' }],
        whyChooseTitle: 'لماذا تختار ألواح GIPSOVINIL.KZ؟',
        whyChooseText: 'تجمع ألواحنا بين العملية والجمالية: فهي حل متكامل جاهز يقلّص وقت التشطيب ويرفع من متانة الأسطح. تحافظ الألواح ذات الطلاء الزخرفي من PVC على مظهرها الأنيق، وسهلة التنظيف، وتدوم لسنوات طويلة حتى في ظروف الاستخدام المكثّف.'
      }
    }
  };

  const product = productData[productId];
  const t = labels[language] || labels.en || labels.ru;

  if (!product) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-[#061d36] mb-4">Product not found</p>
          <Link to="/products" className="text-blue-600 hover:underline">
            {t.back}
          </Link>
        </div>
      </div>
    );
  }

  const p = product[language] || product.en || product.ru;
  
  // Get SEO content for current product and language
  const seo = seoContent[productId] ? seoContent[productId][language] : null;

  // Helper to get icon component
  const getIconComponent = (iconName) => {
    const icons = {
      maximize: Maximize,
      layers: Layers,
      flame: Flame,
      droplets: Droplets,
      volume: Volume2,
      weight: Scale,
      shield: Shield,
      scale: Scale,
      tag: Tag,
      file: FileText
    };
    return icons[iconName] || Layers;
  };

  return (
    <div className="pt-20 bg-[#061d36]" data-testid="product-detail-page">
      {/* Header */}
      <section className="py-8 bg-[#061d36] text-white">
        <div className="container mx-auto px-4">
          <Link 
            to="/products" 
            className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{p.fullTitle}</h1>
          <p className="text-lg text-blue-200 max-w-3xl">{p.description}</p>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-4 max-w-6xl">
            {p.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full whitespace-nowrap">
                <CheckCircle2 className="w-5 h-5 text-[#061d36]" />
                <span className="font-medium text-[#061d36]">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Description - Only for GKL */}
      {productId === 'gkl' && seo && (
        <section className="py-8 bg-[#F5F5F0]">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl">
              <h2 className="text-2xl font-bold text-[#061d36] mb-6">
                {seo.title}
              </h2>
              <p className="text-lg text-[#061d36] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seo.desc1) }} />
              <p className="text-lg text-[#061d36] leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seo.desc2) }} />
            </div>
          </div>
        </section>
      )}

      {/* Main Description - Only for Comfort */}
      {productId === 'comfort' && seo && (
        <section className="py-8 bg-[#F5F5F0]">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl">
              <h2 className="text-2xl font-bold text-[#061d36] mb-6">
                {seo.title}
              </h2>
              <p className="text-lg text-[#061d36] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seo.desc1) }} />
              <p className="text-lg text-[#061d36] leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seo.desc2) }} />
            </div>
          </div>
        </section>
      )}

      {/* Main Description - Only for SML */}
      {productId === 'sml' && seo && (
        <section className="py-8 bg-[#F5F5F0]">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl">
              <h2 className="text-2xl font-bold text-[#061d36] mb-6">
                {seo.title}
              </h2>
              <p className="text-lg text-[#061d36] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seo.desc1) }} />
              <p className="text-lg text-[#061d36] leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seo.desc2) }} />
            </div>
          </div>
        </section>
      )}

      {/* Technical Specifications - New Design */}
      <section className="py-8 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#061d36] mb-8">{t.techSpecs}</h2>
          
          <div className="grid lg:grid-cols-2 gap-6 max-w-6xl items-stretch">
            {/* Key Specs Card - Visual */}
            <div className="bg-[#061d36] rounded-2xl overflow-hidden p-6">
              <h3 className="text-xl font-bold text-white mb-4">{t.keySpecs}</h3>
              
              {/* Key Specs Grid */}
              <div className="grid grid-cols-2 gap-3">
                {p.keySpecs.map((spec, idx) => {
                  const IconComponent = getIconComponent(spec.icon);
                  return (
                    <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-4 h-4 text-blue-300" />
                        </div>
                        <span className="text-blue-200 text-xs uppercase tracking-wide">{spec.label}</span>
                      </div>
                      <p className="text-white font-bold text-lg">{spec.value}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Product Image + Specs Table */}
            <div className="flex flex-col h-full">
              <div className="w-full rounded-2xl overflow-hidden border-2 border-[#061d36] bg-[#F5F5F0] flex flex-col flex-1">
                <div className="flex-1 flex items-center justify-center overflow-hidden min-h-0">
                  <img 
                    src={productId === 'comfort' ? '/images/comfort_layers.webp' : productId === 'sml' ? '/images/sml_layers.webp' : '/images/gkl_layers.webp'} 
                    alt={p.title} 
                    className="w-full h-full object-contain p-3" 
                  />
                </div>
                <table className="w-full mt-auto" data-testid="detailed-specs-table">
                  <tbody>
                    {p.detailedSpecs.map((spec, idx) => (
                      <tr key={idx} className="border-t border-[#061d36]/15">
                        <td className="py-2.5 px-5 font-medium text-[#061d36] text-sm border-r border-[#061d36]/15 w-1/2">
                          {spec.label}
                        </td>
                        <td className="py-2.5 px-5 text-[#061d36]/80 text-sm w-1/2">
                          {spec.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* SEO Content Section - Only for GKL */}
      {productId === 'gkl' && seo && (
        <section className="py-12 bg-[#F5F5F0]">
          <div className="container mx-auto px-4">
            {/* Advantages */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-8">
                {seo.advantagesTitle}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {seo.advantages.map((adv, idx) => {
                  const icons = [Sparkles, Shield, Flame, Droplets, CheckCircle2, Layers];
                  const IconComponent = icons[idx % icons.length];
                  return (
                    <div key={idx} className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 bg-[#061d36] rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-[#061d36] mb-2">{adv.title}</h4>
                      <p className="text-[#061d36]/70 text-sm">{adv.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Application Areas */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-6">
                {seo.applicationsTitle}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {seo.applications.map((item, idx) => (
                  <AppCard key={idx} item={item} idx={idx} productId={productId} isAdmin={isAdmin} appImages={appImages} uploadingIdx={uploadingIdx} onUpload={handleAppImageUpload} onDelete={handleAppImageDelete} onLightbox={(img, text) => { setLightboxLoaded(false); setLightboxImage({ src: img, text }); }} />
                ))}
              </div>
            </div>

            {/* AGSK Nomenclature Table */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-6">
                {language === 'ru' ? 'Номенклатура в АГСК-3' : language === 'kz' ? 'АГСК-3 номенклатурасы' : 'AGSK-3 Nomenclature'}
              </h3>
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#061d36] text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        {language === 'ru' ? 'Код' : language === 'kz' ? 'Код' : 'Code'}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        {language === 'ru' ? 'Наименование' : language === 'kz' ? 'Атауы' : 'Name'}
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        {language === 'ru' ? 'Ед. изм.' : language === 'kz' ? 'Өлш. бір.' : 'Unit'}
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        {language === 'ru' ? 'Каталог цветов' : language === 'kz' ? 'Түстер каталогы' : 'Color Catalog'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-[#061d36] font-mono text-xs">273-203-0101-0001</td>
                      <td className="px-4 py-3 text-[#061d36]/80">Панель отделочная гипсокартонная текстура однотонная RAL 120 микрон с лицевой поверхностью из ПВХ пленки толщиной 12,5 мм</td>
                      <td className="px-4 py-3 text-center text-[#061d36]/70">м²</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col gap-1">
                          <Link to="/catalog?category=eastern" className="font-medium text-xs hover:underline">
                            <span className="text-[#061d36]">Восточные вкусы</span><span className="text-[#8B4444]">→</span>
                          </Link>
                          <Link to="/catalog?category=gray" className="font-medium text-xs hover:underline">
                            <span className="text-[#061d36]">5 оттенков серого</span><span className="text-[#8B4444]">→</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 bg-gray-50/50">
                      <td className="px-4 py-3 text-[#061d36] font-mono text-xs">273-203-0101-0002</td>
                      <td className="px-4 py-3 text-[#061d36]/80">Панель отделочная гипсокартонная текстура бетон 120 микрон с лицевой поверхностью из ПВХ пленки толщиной 12,5 мм</td>
                      <td className="px-4 py-3 text-center text-[#061d36]/70">м²</td>
                      <td className="px-4 py-3 text-center">
                        <Link to="/catalog?category=standard" className="font-medium text-sm hover:underline">
                          <span className="text-[#061d36]">Стандарт</span><span className="text-[#8B4444]">→</span>
                        </Link>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-[#061d36] font-mono text-xs">273-203-0101-0003</td>
                      <td className="px-4 py-3 text-[#061d36]/80">Панель отделочная гипсокартонная текстура дерево 150 микрон с лицевой поверхностью из ПВХ пленки толщиной 12,5 мм</td>
                      <td className="px-4 py-3 text-center text-[#061d36]/70">м²</td>
                      <td className="px-4 py-3 text-center">
                        <Link to="/catalog?category=standard" className="font-medium text-sm hover:underline">
                          <span className="text-[#061d36]">Стандарт</span><span className="text-[#8B4444]">→</span>
                        </Link>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 bg-gray-50/50">
                      <td className="px-4 py-3 text-[#061d36] font-mono text-xs">273-203-0101-0004</td>
                      <td className="px-4 py-3 text-[#061d36]/80">Панель отделочная гипсокартонная текстура лофт, бетон 180 микрон с лицевой поверхностью из ПВХ пленки толщиной 12,5 мм</td>
                      <td className="px-4 py-3 text-center text-[#061d36]/70">м²</td>
                      <td className="px-4 py-3 text-center">
                        <Link to="/catalog?category=premium" className="font-medium text-sm hover:underline">
                          <span className="text-[#061d36]">Премиум</span><span className="text-[#8B4444]">→</span>
                        </Link>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-[#061d36] font-mono text-xs">273-203-0101-0005</td>
                      <td className="px-4 py-3 text-[#061d36]/80">Панель отделочная гипсокартонная премиум 250 микрон с лицевой поверхностью из ПВХ пленки толщиной 12,5 мм</td>
                      <td className="px-4 py-3 text-center text-[#061d36]/70">м²</td>
                      <td className="px-4 py-3 text-center">
                        <Link to="/catalog?category=superpremium" className="font-medium text-sm hover:underline">
                          <span className="text-[#061d36]">Супер премиум</span><span className="text-[#8B4444]">→</span>
                        </Link>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 bg-gray-50/50">
                      <td className="px-4 py-3 text-[#061d36] font-mono text-xs">273-203-0101-0006</td>
                      <td className="px-4 py-3 text-[#061d36]/80">Панель отделочная гипсокартонная luxe 250 микрон с лицевой поверхностью из ПВХ пленки толщиной 12,5 мм</td>
                      <td className="px-4 py-3 text-center text-[#061d36]/70">м²</td>
                      <td className="px-4 py-3 text-center">
                        <Link to="/catalog?category=luxe" className="font-medium text-sm hover:underline">
                          <span className="text-[#061d36]">Люкс</span><span className="text-[#8B4444]">→</span>
                        </Link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose - Full width dark section */}
      {productId === 'gkl' && seo && (
        <section className="py-12 bg-[#061d36]">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl">
              <h3 className="text-2xl font-bold mb-4 text-white text-center">
                {seo.whyChooseTitle}
              </h3>
              <p className="text-xl text-white leading-relaxed text-center">
                {seo.whyChooseText}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* SEO Content Section - Only for Comfort */}
      {productId === 'comfort' && seo && (
        <section className="py-12 bg-[#F5F5F0]">
          <div className="container mx-auto px-4">
            {/* Advantages */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-8">
                {seo.advantagesTitle}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {seo.advantages.map((adv, idx) => {
                  const iconMap = { shield: Shield, flame: Flame, droplets: Droplets, wrench: Wrench, check: CheckCircle2, layers: Layers };
                  const IconComponent = iconMap[adv.icon] || Shield;
                  return (
                    <div key={idx} className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 bg-[#061d36] rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-[#061d36] mb-2">{adv.title}</h4>
                      <p className="text-[#061d36]/70 text-sm">{adv.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Application Areas */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-6">
                {seo.applicationsTitle}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {seo.applications.map((item, idx) => (
                  <AppCard key={idx} item={item} idx={idx} productId={productId} isAdmin={isAdmin} appImages={appImages} uploadingIdx={uploadingIdx} onUpload={handleAppImageUpload} onDelete={handleAppImageDelete} onLightbox={(img, text) => { setLightboxLoaded(false); setLightboxImage({ src: img, text }); }} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose - Full width dark section - Comfort */}
      {productId === 'comfort' && seo && (
        <section className="py-12 bg-[#061d36]">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl">
              <h3 className="text-2xl font-bold mb-4 text-white text-center">
                {seo.whyChooseTitle}
              </h3>
              <p className="text-xl text-white leading-relaxed text-center">
                {seo.whyChooseText}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* SEO Content Section - Only for SML */}
      {productId === 'sml' && seo && (
        <section className="py-12 bg-[#F5F5F0]">
          <div className="container mx-auto px-4">
            {/* Advantages */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-8">
                {seo.advantagesTitle}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {seo.advantages.map((adv, idx) => {
                  const iconMap = { flame: Flame, droplets: Droplets, shield: Shield, wrench: Wrench, volume: Volume2, layers: Layers };
                  const IconComponent = iconMap[adv.icon] || Shield;
                  return (
                    <div key={idx} className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 bg-[#061d36] rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-[#061d36] mb-2">{adv.title}</h4>
                      <p className="text-[#061d36]/70 text-sm">{adv.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Application Areas */}
            <div className="max-w-6xl mb-12">
              <h3 className="text-2xl font-bold text-[#061d36] mb-6">
                {seo.applicationsTitle}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {seo.applications.map((item, idx) => (
                  <AppCard key={idx} item={item} idx={idx} productId={productId} isAdmin={isAdmin} appImages={appImages} uploadingIdx={uploadingIdx} onUpload={handleAppImageUpload} onDelete={handleAppImageDelete} onLightbox={(img, text) => { setLightboxLoaded(false); setLightboxImage({ src: img, text }); }} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose - Full width dark section - SML */}
      {productId === 'sml' && seo && (
        <section className="py-12 bg-[#061d36]">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl">
              <h3 className="text-2xl font-bold mb-4 text-white text-center">
                {seo.whyChooseTitle}
              </h3>
              <p className="text-xl text-white leading-relaxed text-center">
                {seo.whyChooseText}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-[#061d36]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-all"
            >
              {t.contact}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/catalog"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 hover:border-white/50 hover:bg-white/5 text-white rounded-xl font-semibold transition-all"
            >
              {t.catalog}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox-modal"
        >
          <button 
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-5xl max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            {!lightboxLoaded && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                <p className="text-white/60 text-sm">{language === 'ru' ? 'Загрузка...' : language === 'kz' ? 'Жүктелуде...' : 'Loading...'}</p>
              </div>
            )}
            <img 
              src={lightboxImage.src} 
              alt={lightboxImage.text} 
              className={`max-w-full max-h-[75vh] object-contain rounded-lg transition-opacity duration-300 ${lightboxLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
              onLoad={() => setLightboxLoaded(true)}
            />
            {lightboxLoaded && <p className="text-white text-lg font-medium mt-4">{lightboxImage.text}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
